import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, CheckCircle, Clock, Warning, CalendarBlank, User, ListBullets, Kanban, Phone, EnvelopeSimple, ChatCircle, FileText, ArrowsCounterClockwise, ClipboardText, CurrencyDollar, ArrowClockwise, MagnifyingGlass, Scales, Gavel, PaperPlaneTilt, DotsThreeCircle, ListChecks} from '@phosphor-icons/react';
import { getTasks, createTask, updateTask, deleteTask, getDossiers, getOrganizations, getContacts } from '../services/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../utils/apiError';
import { AIAssistButton } from '../components/AIAssistButton';
import { DataTable } from '../components/DataTable';
import { KanbanBoard } from '../components/KanbanBoard';
import { PageHero } from '../components/motion/PageHero';

const typeTacheOptions = [
  { value: 'relance', label: 'Relance', Icon: ArrowClockwise, iconColor: '#EA580C' },
  { value: 'demande_piece', label: 'Demande de pièce', Icon: FileText, iconColor: '#B45309' },
  { value: 'verification_dossier', label: 'Vérification dossier', Icon: MagnifyingGlass, iconColor: '#2563EB' },
  { value: 'revue_juridique', label: 'Revue juridique', Icon: Gavel, iconColor: '#7C3AED' },
  { value: 'appel', label: 'Appel', Icon: Phone, iconColor: '#059669' },
  { value: 'rendez_vous', label: 'Rendez-vous', Icon: CalendarBlank, iconColor: '#2D5A4C' },
  { value: 'preparation_document', label: 'Préparation document', Icon: ClipboardText, iconColor: '#0284C7' },
  { value: 'verification_paiement', label: 'Vérification paiement', Icon: CurrencyDollar, iconColor: '#059669' },
  { value: 'renouvellement', label: 'Renouvellement', Icon: ArrowsCounterClockwise, iconColor: '#0891B2' },
  { value: 'suivi', label: 'Suivi post-activation', Icon: ChatCircle, iconColor: '#5E6D66' },
  { value: 'email', label: 'Email', Icon: EnvelopeSimple, iconColor: '#2563EB' },
  { value: 'envoi_courrier', label: 'Envoi courrier', Icon: PaperPlaneTilt, iconColor: '#B45309' },
  { value: 'autre', label: 'Autre', Icon: DotsThreeCircle, iconColor: '#6B7280' },
];

const statutOptions = [
  { value: 'a_faire', label: 'À faire', color: 'bg-blue-100 text-blue-800' },
  { value: 'en_cours', label: 'En cours', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'en_attente', label: 'En attente', color: 'bg-orange-100 text-orange-800' },
  { value: 'terminee', label: 'Terminée', color: 'bg-green-100 text-green-800' },
  { value: 'annulee', label: 'Annulée', color: 'bg-gray-100 text-gray-800' },
];

const prioriteOptions = [
  { value: 'basse', label: 'Basse', color: 'text-gray-500' },
  { value: 'normale', label: 'Normale', color: 'text-blue-500' },
  { value: 'haute', label: 'Haute', color: 'text-orange-500' },
  { value: 'critique', label: 'Critique', color: 'text-red-500' },
];

export function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState('');
  const [filterPriorite, setFilterPriorite] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'kanban'
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    type_tache: '',
    dossier_id: '',
    organization_id: '',
    contact_id: '',
    priorite: 'normale',
    date_echeance: '',
    heure: '',
    statut: 'a_faire',
  });

  const fetchData = async () => {
    try {
      const params = {};
      if (filterStatut) params.statut = filterStatut;
      if (filterPriorite) params.priorite = filterPriorite;
      
      const [tasksRes, dossiersRes, orgsRes, contactsRes] = await Promise.all([
        getTasks(params),
        getDossiers(),
        getOrganizations(),
        getContacts()
      ]);
      
      let tasksList = tasksRes.data.items || [];
      if (!showCompleted) {
        tasksList = tasksList.filter(t => t.statut !== 'terminee' && t.statut !== 'annulee');
      }
      
      setTasks(tasksList);
      setDossiers(dossiersRes.data.items || []);
      setOrganizations(orgsRes.data.items || []);
      setContacts(contactsRes.data.items || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterStatut, filterPriorite, showCompleted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await updateTask(editingTask.id, formData);
        toast.success('Tâche mise à jour');
      } else {
        await createTask(formData);
        toast.success('Tâche créée');
      }
      setDialogOpen(false);
      setEditingTask(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Erreur'));
    }
  };

  const resetForm = () => {
    setFormData({
      titre: '',
      description: '',
      type_tache: '',
      dossier_id: '',
      organization_id: '',
      contact_id: '',
      priorite: 'normale',
      date_echeance: '',
      heure: '',
      statut: 'a_faire',
    });
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      titre: task.titre || '',
      description: task.description || '',
      type_tache: task.type_tache || '',
      dossier_id: task.dossier_id || '',
      organization_id: task.organization_id || '',
      contact_id: task.contact_id || '',
      priorite: task.priorite || 'normale',
      date_echeance: task.date_echeance || '',
      heure: task.heure || '',
      statut: task.statut || 'a_faire',
    });
    setDialogOpen(true);
  };

  const handleToggleComplete = async (task) => {
    try {
      const newStatut = task.statut === 'terminee' ? 'a_faire' : 'terminee';
      await updateTask(task.id, { statut: newStatut });
      toast.success(newStatut === 'terminee' ? 'Tache terminee' : 'Tache reouverte');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleKanbanUpdate = async (taskId, updates) => {
    try {
      await updateTask(taskId, updates);
      // Optimistic update
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updates } : t));
      const statusLabel = statutOptions.find(s => s.value === updates.statut)?.label || updates.statut;
      toast.success(`Tache deplacee vers "${statusLabel}"`);
    } catch {
      toast.error('Erreur lors du deplacement');
      fetchData();
    }
  };

  const openNewDialog = () => {
    setEditingTask(null);
    resetForm();
    setDialogOpen(true);
  };

  // Keyboard shortcut: 'n' opens new task dialog
  useEffect(() => {
    const handleNew = () => { setEditingTask(null); resetForm(); setDialogOpen(true); };
    window.addEventListener('crm-shortcut-new', handleNew);
    return () => window.removeEventListener('crm-shortcut-new', handleNew);
  }, []);

  const getDossierNum = (id) => dossiers.find(d => d.id === id)?.numero_dossier || '';
  const getOrgName = (id) => organizations.find(o => o.id === id)?.raison_sociale || '';
  const getContactName = (id) => {
    const c = contacts.find(c => c.id === id);
    return c ? `${c.prenom} ${c.nom}` : '';
  };

  const isOverdue = (date) => {
    if (!date) return false;
    return new Date(date) < new Date() && new Date(date).toDateString() !== new Date().toDateString();
  };

  const isToday = (date) => {
    if (!date) return false;
    return new Date(date).toDateString() === new Date().toDateString();
  };

  // Stats
  const todayTasks = tasks.filter(t => isToday(t.date_echeance) && t.statut !== 'terminee');
  const overdueTasks = tasks.filter(t => isOverdue(t.date_echeance) && t.statut !== 'terminee');

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Tâches"
        subtitle="Priorités, échéances et assignations d'équipe"
        icon={<ListChecks size={24} weight="duotone" className="text-white" />}
        gradient="from-[#00AEEF] to-[#2D5A4C]"
        accents={['#80C51F', '#FF8E00']}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CalendarBlank size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2421]">{todayTasks.length}</p>
              <p className="text-xs text-[#5E6D66]">Aujourd'hui</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Warning size={24} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#D47A62]">{overdueTasks.length}</p>
              <p className="text-xs text-[#5E6D66]">En retard</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Clock size={24} className="text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2421]">{tasks.filter(t => t.statut !== 'terminee').length}</p>
              <p className="text-xs text-[#5E6D66]">Total actives</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <Select value={filterStatut} onValueChange={(v) => setFilterStatut(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px] border-[#E8E6E1]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {statutOptions.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterPriorite} onValueChange={(v) => setFilterPriorite(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px] border-[#E8E6E1]">
                <SelectValue placeholder="Priorité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {prioriteOptions.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="showCompleted" 
                checked={showCompleted} 
                onCheckedChange={setShowCompleted}
              />
              <Label htmlFor="showCompleted" className="text-sm cursor-pointer">
                Afficher les terminées
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tasks View */}
      {viewMode === 'kanban' ? (
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
            </div>
          ) : tasks.length === 0 ? (
            <Card className="border-[#E8E6E1]">
              <CardContent className="p-4 text-center py-12">
                <p className="text-[#5E6D66]">Aucune tache trouvee</p>
                <Button onClick={openNewDialog} variant="link" className="text-[#2D5A4C] mt-2">
                  Creer votre premiere tache
                </Button>
              </CardContent>
            </Card>
          ) : (
            <KanbanBoard
              tasks={tasks}
              onUpdateTask={handleKanbanUpdate}
              onEditTask={handleEdit}
              prioriteOptions={prioriteOptions}
              isOverdue={isOverdue}
              isToday={isToday}
            />
          )}
        </div>
      ) : (
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#5E6D66]">Aucune tache trouvee</p>
              <Button onClick={openNewDialog} variant="link" className="text-[#2D5A4C] mt-2">
                Creer votre premiere tache
              </Button>
            </div>
          ) : (
            <DataTable
              data={tasks}
              columns={[
                {
                  key: 'check',
                  label: '',
                  sortable: false,
                  width: '40px',
                  render: (_, row) => (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={row.statut === 'terminee'}
                        onCheckedChange={() => handleToggleComplete(row)}
                        data-testid={`complete-task-${row.id}`}
                      />
                    </div>
                  ),
                },
                {
                  key: 'titre',
                  label: 'Tache',
                  render: (val, row) => (
                    <div>
                      <p className={`font-medium text-[#1A2421] ${row.statut === 'terminee' ? 'line-through opacity-50' : ''}`}>{val}</p>
                      {row.description && <p className="text-xs text-[#5E6D66] line-clamp-1">{row.description}</p>}
                    </div>
                  ),
                },
                {
                  key: 'type_tache',
                  label: 'Type',
                  render: (val) => {
                    const t = typeTacheOptions.find(o => o.value === val);
                    const I = t?.Icon;
                    return (
                      <Badge variant="outline" className="text-xs gap-1">
                        {I && <I size={11} style={{ color: t.iconColor }} weight="duotone" />}
                        {t?.label || val || '-'}
                      </Badge>
                    );
                  },
                },
                {
                  key: 'dossier_id',
                  label: 'Lie a',
                  sortable: false,
                  render: (_, row) => (
                    <div className="text-xs text-[#5E6D66]">
                      {row.dossier_id && <p>Dossier: {getDossierNum(row.dossier_id)}</p>}
                      {row.organization_id && <p>{getOrgName(row.organization_id)}</p>}
                      {row.contact_id && <p className="flex items-center gap-1"><User size={10} /> {getContactName(row.contact_id)}</p>}
                    </div>
                  ),
                },
                {
                  key: 'date_echeance',
                  label: 'Echeance',
                  render: (val, row) => val ? (
                    <div className={`text-sm ${isOverdue(val) && row.statut !== 'terminee' ? 'text-red-500 font-medium' : isToday(val) ? 'text-orange-500 font-medium' : 'text-[#5E6D66]'}`}>
                      {new Date(val).toLocaleDateString('fr-FR')}
                      {row.heure && <span className="ml-1">{row.heure}</span>}
                      {isOverdue(val) && row.statut !== 'terminee' && <Badge className="ml-2 bg-red-100 text-red-800 text-xs">En retard</Badge>}
                    </div>
                  ) : '-',
                },
                {
                  key: 'priorite',
                  label: 'Priorite',
                  render: (val) => (
                    <span className={`text-sm font-medium ${prioriteOptions.find(p => p.value === val)?.color || ''}`}>
                      {prioriteOptions.find(p => p.value === val)?.label || val}
                    </span>
                  ),
                },
                {
                  key: 'statut',
                  label: 'Statut',
                  render: (val) => (
                    <Badge className={statutOptions.find(s => s.value === val)?.color || ''}>
                      {statutOptions.find(s => s.value === val)?.label || val}
                    </Badge>
                  ),
                },
              ]}
              searchKeys={['titre', 'description', 'type_tache']}
              showSearch={false}
              onRowClick={(row) => handleEdit(row)}
              emptyMessage="Aucune tache trouvee"
              data-testid="tasks-datatable"
              mobileCardRender={(row) => (
                <Card className={`border-[#E8E6E1] transition-colors active:bg-[#F9F8F5] ${row.statut === 'terminee' ? 'opacity-60' : ''}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start gap-2.5">
                      <div onClick={e => e.stopPropagation()} className="mt-0.5">
                        <Checkbox checked={row.statut === 'terminee'} onCheckedChange={() => handleToggleComplete(row)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium text-[#1A2421] ${row.statut === 'terminee' ? 'line-through' : ''}`}>{row.titre}</p>
                        {row.description && <p className="text-[10px] text-[#5E6D66] line-clamp-1 mt-0.5">{row.description}</p>}
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          <span className={`text-[10px] font-medium ${prioriteOptions.find(p => p.value === row.priorite)?.color || ''}`}>
                            {prioriteOptions.find(p => p.value === row.priorite)?.label || row.priorite}
                          </span>
                          {row.date_echeance && (
                            <span className={`text-[10px] ${isOverdue(row.date_echeance) && row.statut !== 'terminee' ? 'text-red-500 font-bold' : 'text-[#5E6D66]'}`}>
                              {new Date(row.date_echeance).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                          <Badge className={`text-[8px] ${statutOptions.find(s => s.value === row.statut)?.color || ''}`}>
                            {statutOptions.find(s => s.value === row.statut)?.label || row.statut}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            />
          )}
        </CardContent>
      </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editingTask ? 'Modifier la tâche' : 'Nouvelle tâche'}
            </DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                required
                placeholder="Ex: Relancer pour documents manquants"
                data-testid="input-titre"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Description</Label>
                <AIAssistButton
                  value={formData.description}
                  field="Description d'une tâche CRM"
                  onApply={(t) => setFormData({ ...formData, description: t })}
                  testId="ai-assist-task-desc"
                />
              </div>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                placeholder="Détails optionnels..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de tâche</Label>
                <Select value={formData.type_tache} onValueChange={(v) => setFormData({ ...formData, type_tache: v })}>
                  <SelectTrigger data-testid="task-type-select">
                    <SelectValue placeholder="Sélectionner">
                      {formData.type_tache ? (() => {
                        const t = typeTacheOptions.find(o => o.value === formData.type_tache);
                        const I = t?.Icon;
                        return (
                          <span className="flex items-center gap-2">
                            {I && <I size={14} style={{ color: t.iconColor }} weight="duotone" />}
                            {t?.label || formData.type_tache}
                          </span>
                        );
                      })() : null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {typeTacheOptions.map(t => {
                      const I = t.Icon;
                      return (
                        <SelectItem key={t.value} value={t.value} data-testid={`task-type-opt-${t.value}`}>
                          <span className="flex items-center gap-2">
                            {I && <I size={14} style={{ color: t.iconColor }} weight="duotone" />}
                            {t.label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priorité</Label>
                <Select value={formData.priorite} onValueChange={(v) => setFormData({ ...formData, priorite: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {prioriteOptions.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date d'échéance</Label>
                <Input
                  type="date"
                  value={formData.date_echeance}
                  onChange={(e) => setFormData({ ...formData, date_echeance: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Heure</Label>
                <Input
                  type="time"
                  value={formData.heure}
                  onChange={(e) => setFormData({ ...formData, heure: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Dossier lié</Label>
              <Select value={formData.dossier_id} onValueChange={(v) => setFormData({ ...formData, dossier_id: v === 'none' ? '' : v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Aucun" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {dossiers.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.numero_dossier}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {editingTask && (
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={formData.statut} onValueChange={(v) => setFormData({ ...formData, statut: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statutOptions.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditingTask(null); resetForm(); }}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-task-btn">
                {editingTask ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
